import { RefObject, useEffect, useRef } from 'react'

export interface UseOutsideClick<TRef extends Node> {
  elementRef: RefObject<TRef>
}

function useOutsideClick<TRef extends Node>(
  handler: () => void,
  options?: {
    refs?: Array<RefObject<Node>> //Additional refs to consider as inside clicks.
    shouldIgnore?: (event: MouseEvent) => boolean
  },
): UseOutsideClick<TRef> {
  const elementRef = useRef<TRef>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const targetNode = event.target as Node | null

      // Check if the click should be ignored based on the custom function
      if (options?.shouldIgnore && options.shouldIgnore(event)) {
        return
      }

      // Check if the click is inside the main ref
      if (elementRef.current && targetNode && elementRef.current.contains(targetNode)) {
        return
      }

      // Check if the click is inside any of the additional refs
      if (
        options?.refs &&
        options.refs.some((ref) => ref.current && ref.current.contains(targetNode))
      ) {
        return
      }

      // If the click is outside all specified elements, call the handler
      handler()
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handler, options])

  return { elementRef }
}

export default useOutsideClick
